import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  MenuItem,
  Select,
  Stack,
  Badge,
  Avatar,
  Chip,
  Tooltip,
  Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CallIcon from '@mui/icons-material/Call';
import EmailIcon from '@mui/icons-material/Email';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { NotificationsAPI } from '../../api/Endpoints/NotificationsAPI';
import { toast } from 'react-toastify';

export default function NotificationsDrawer({ open, onClose, data, refreshNotifications }) {
  const [tabValue, setTabValue] = useState(0);
  const [typeFilter, setTypeFilter] = useState('all');

  const handleTabChange = (event, newValue) => setTabValue(newValue);

  const getFilteredData = () => {
    if (!data) return [];

    let timeKey = 'history';
    if (tabValue === 0) timeKey = 'today';
    else if (tabValue === 1) timeKey = 'upcoming';

    const followUpItems = data.followup?.[timeKey]?.leads || [];
    const mappedFollowUps = followUpItems.map(item => ({
      id: item.id,
      lead_id: item.id,
      date: item.follow_up_date,
      name: item.name || 'N/A',
      remarks: item.follow_up_remarks || item.remarks || 'No remarks',
      phone: item.phone_number || 'N/A',
      email: item.email || 'N/A',
      is_read: item.follow_up_is_read || 0,
      type: 'follow-up'
    }));

    const siteVisitItems = data.sitevisit?.[timeKey]?.visits || [];
    const mappedSiteVisits = siteVisitItems.map(item => ({
      id: item.id,
      lead_id: item.lead_id,
      date: item.site_visit_date,
      name: item.lead?.name || 'N/A',
      remarks: item.site_visit_remarks || item.remarks || 'No remarks',
      phone: item.lead?.phone_number || 'N/A',
      email: item.lead?.email || 'N/A',
      is_read: item.is_read || 0,
      type: 'site-visit'
    }));

    let results = [];
    if (typeFilter === 'all') {
      results = [...mappedSiteVisits, ...mappedFollowUps];
      results.sort((a, b) => new Date(b.date) - new Date(a.date));
    } else if (typeFilter === 'follow-ups') {
      results = mappedFollowUps;
    } else {
      results = mappedSiteVisits;
    }

    return results;
  };

  const displayData = getFilteredData();
  const unreadCount = displayData.filter(item => item.is_read === 0).length;

  const handleMarkAllRead = () => {
    let requests = [];
    if (typeFilter === 'all' || typeFilter === 'follow-ups')
      requests.push(NotificationsAPI.getScheduledNotifications('followup'));
    if (typeFilter === 'all' || typeFilter === 'site-visits')
      requests.push(NotificationsAPI.getScheduledNotifications('sitevisit'));

    Promise.all(requests)
      .then(() => {
        toast.success(`All items marked as read`);
        if (refreshNotifications) refreshNotifications();
      })
      .catch((err) => console.error(err));
  };

  const handleMarkAsRead = (id, type) => {
    const apiType = type === 'follow-up' ? 'followup' : 'sitevisit';
    NotificationsAPI.markAsRead(apiType, id)
      .then((res) => {
        if (res.data.status) {
          toast.success("Marked as read");
          if (refreshNotifications) refreshNotifications();
        }
      })
      .catch((err) => console.error(err));
  };

  const getDateColor = (date, isRead) => {
    if (isRead === 1) return '#9CA3AF';
    const today = new Date().toDateString();
    const itemDate = new Date(date).toDateString();
    if (itemDate === today) return '#2563EB';
    if (new Date(date) < new Date()) return '#DC2626';
    return '#059669';
  };

  const formatDisplayDate = (date) => {
    if (!date) return 'No date';
    const today = new Date().toDateString();
    const itemDate = new Date(date).toDateString();
    const d = new Date(date);
    const formatted = `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
    if (itemDate === today) return `Today · ${formatted}`;
    if (tabValue !== 2 && new Date(date) < new Date()) return `Overdue · ${formatted}`;
    return formatted;
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 380 },
          bgcolor: '#F9FAFB',
          borderTopLeftRadius: { sm: 12 },
          borderBottomLeftRadius: { sm: 12 },
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* Header */}
      <Box sx={{
        px: 2, py: 1.5,
        display: 'flex',
        alignItems: 'center',
        bgcolor: '#FFFFFF',
        borderBottom: '1px solid #E5E7EB',
        minHeight: 52
      }}>
        <IconButton onClick={onClose} size="small" sx={{ mr: 1.5, color: '#6B7280' }}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography sx={{ flexGrow: 1, textAlign: 'center', fontWeight: 600, color: '#111827', fontSize: '0.95rem' }}>
          Schedule
        </Typography>
        <Box sx={{ width: 32 }} />
      </Box>

      {/* Tabs + Filter Row */}
      <Box sx={{ bgcolor: '#FFFFFF', borderBottom: '1px solid #E5E7EB', px: 2, pt: 1.5, pb: 1 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            mb: 1.5,
            bgcolor: '#F3F4F6',
            borderRadius: 2,
            minHeight: 36,
            p: 0.4,
            '& .MuiTab-root': {
              minHeight: 32,
              textTransform: 'none',
              color: '#6B7280',
              fontWeight: 500,
              fontSize: '0.8rem',
              borderRadius: 1.5,
              py: 0
            },
            '& .Mui-selected': {
              color: '#111827 !important',
              fontWeight: 600,
              bgcolor: '#FFFFFF',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
            },
            '& .MuiTabs-indicator': { display: 'none' }
          }}
        >
          <Tab label="Today" />
          <Tab label="Upcoming" />
          <Tab label="History" />
        </Tabs>

        {/* Filter + Mark all row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Select
              size="small"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              sx={{
                bgcolor: '#F3F4F6',
                borderRadius: 1.5,
                height: 30,
                minWidth: 110,
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '& .MuiSelect-select': { py: 0, px: 1.5, fontSize: '0.78rem', fontWeight: 500 }
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="follow-ups">Follow-ups</MenuItem>
              <MenuItem value="site-visits">Site Visits</MenuItem>
            </Select>
            {unreadCount > 0 && (
              <Chip
                label={`${unreadCount} unread`}
                size="small"
                sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#DBEAFE', color: '#2563EB', fontWeight: 600 }}
              />
            )}
          </Box>
          {tabValue === 0 && unreadCount > 0 && (
            <Typography
              onClick={handleMarkAllRead}
              sx={{
                display: 'inline-flex', alignItems: 'center', cursor: 'pointer',
                color: '#2563EB', fontWeight: 500, fontSize: '0.75rem',
                '&:hover': { color: '#1D4ED8', textDecoration: 'underline' }
              }}
            >
              <DoneAllIcon sx={{ fontSize: 13, mr: 0.4 }} />
              Mark all read
            </Typography>
          )}
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{
        flex: 1,
        overflowY: 'auto',
        px: 1.5,
        py: 1.5,
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-thumb': { background: '#D1D5DB', borderRadius: '4px' },
      }}>
        {displayData.length === 0 ? (
          <Box sx={{
            textAlign: 'center', py: 5, px: 2,
            bgcolor: '#FFFFFF', borderRadius: 2,
            border: '1px dashed #E5E7EB', mt: 1
          }}>
            <CalendarTodayIcon sx={{ fontSize: 36, color: '#D1D5DB', mb: 1 }} />
            <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 500 }}>
              No {tabValue === 0 ? "today's" : tabValue === 1 ? 'upcoming' : 'history'} items
            </Typography>
            <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
              {typeFilter === 'all' ? 'Site visits & Follow-ups' : typeFilter === 'follow-ups' ? 'Follow-ups' : 'Site visits'} will appear here
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {displayData.map((item) => (
              <Box
                key={item.id}
                sx={{
                  bgcolor: item.is_read === 0 ? '#FFFFFF' : '#F9FAFB',
                  border: '1px solid',
                  borderColor: item.is_read === 0 ? '#E5E7EB' : '#F3F4F6',
                  borderRadius: 2,
                  p: 1.5,
                  opacity: item.is_read === 1 ? 0.75 : 1,
                  transition: 'all 0.15s',
                  '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderColor: '#D1D5DB' }
                }}
              >
                {/* Top row: date chip + type badge + mark-read btn */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                      <EventNoteIcon sx={{ fontSize: 12, color: getDateColor(item.date, item.is_read) }} />
                      <Typography sx={{
                        fontSize: '0.72rem', fontWeight: 600,
                        color: getDateColor(item.date, item.is_read)
                      }}>
                        {formatDisplayDate(item.date)}
                      </Typography>
                    </Box>
                    {typeFilter === 'all' && (
                      <Chip
                        label={item.type === 'site-visit' ? 'Site Visit' : 'Follow-up'}
                        size="small"
                        sx={{
                          height: 16, fontSize: '0.65rem',
                          bgcolor: item.type === 'site-visit' ? '#DCFCE7' : '#EEF2FF',
                          color: item.type === 'site-visit' ? '#059669' : '#6366F1',
                          fontWeight: 600, px: 0.5
                        }}
                      />
                    )}
                  </Box>
                  {item.is_read === 0 && tabValue !== 1 && (
                    <Tooltip title="Mark as read" arrow placement="top">
                      <IconButton
                        size="small"
                        onClick={() => handleMarkAsRead(item.id, item.type)}
                        sx={{
                          p: 0.3, color: '#D1D5DB',
                          '&:hover': { color: '#2563EB', bgcolor: '#EFF6FF' }
                        }}
                      >
                        <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                {/* Name + remarks row */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                  <Avatar sx={{
                    width: 28, height: 28,
                    bgcolor: '#E5E7EB', color: '#4B5563',
                    fontSize: '0.75rem', fontWeight: 600,
                    flexShrink: 0, mt: 0.2
                  }}>
                    {item.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      onClick={() => { onClose(); window.open(`/dashboard/lead-details/${item.lead_id}`, '_blank'); }}
                      sx={{
                        fontSize: '0.85rem', fontWeight: 600, color: '#111827',
                        cursor: 'pointer', lineHeight: 1.3,
                        '&:hover': { color: '#2563EB', textDecoration: 'underline' }
                      }}
                    >
                      {item.name}
                    </Typography>
                    <Typography sx={{
                      fontSize: '0.72rem', color: '#6B7280', fontStyle: 'italic',
                      mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      maxWidth: 230
                    }}>
                      "{item.remarks}"
                    </Typography>
                  </Box>
                </Box>

                {/* Contact row */}
                <Box sx={{ display: 'flex', gap: 2, pl: 4.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CallIcon sx={{ fontSize: 11, color: '#9CA3AF' }} />
                    <Typography sx={{ fontSize: '0.72rem', color: '#6B7280' }}>{item.phone}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <EmailIcon sx={{ fontSize: 11, color: '#9CA3AF' }} />
                    <Typography sx={{
                      fontSize: '0.72rem', color: '#6B7280',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160
                    }}>
                      {item.email}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}